import React, { useState, useEffect } from "react";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import Board from "@publicPortal/components/board/Board";
import "./UserSettings.css";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Title } from "@shared/mirage/core/enums";
import { ErrorSharp } from "@mui/icons-material";
import validators, { ValidationResult } from "@shared/mirage/validators";
import { useNavigate } from "react-router-dom";
import { UserService } from "@publicPortal/services/UserService";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { UpdateUserDataFieldValidationResult, UpdateUserDataRequest } from "@shared/mirage/core/contracts/User";
import AddressAutocomplete, { ISelectedAddressRecord } from "@mirage/components/addressBox/AddressAutocomplete";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { PPConstants } from "@shared/publicportal/constants";

const UserSettings = () => {
    const navigate = useNavigate();
    const pp = new PublicPortalRoutingService();
    const userService = new UserService();
    const [firstName, setFirstName] = useState({ value: "", validationResult: { passed: true } } as UserSettingsData);
    const [lastName, setLastName] = useState({ value: "", validationResult: { passed: true } } as UserSettingsData);
    const [address, setAddress] = useState({ value: "", validationResult: { passed: true } } as UserSettingsData);
    const [mobilePhone, setMobilePhone] = useState({ value: "", validationResult: { passed: true } } as UserSettingsData);
    const [title, setTitle] = useState({ value: "" as Title, validationResult: { passed: true } } as UserTitleData);
    const [fieldModified, setFieldModified] = useState(false);
    const [alertData, setAlertData] = useState({ show: false, text: "" });
    const [editMode, setEditMode] = React.useState(false);
    const [customProperties, setCustomProperties] = useState({
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        postTown: "",
        postCode: "",
        uprn: "",
    });

    useEffect(() => {
        const getUserData = async () => {
            const userData = await userService.getUserById();

            userData.first_name && setFirstName({ value: userData.first_name, validationResult: { passed: true } });
            userData.last_name && setLastName({ value: userData.last_name, validationResult: { passed: true } });
            userData.address && setAddress({ value: userData.address, validationResult: { passed: true } });
            userData.mobilePhone && setMobilePhone({ value: userData.mobilePhone, validationResult: { passed: true } });
            userData.title && setTitle({ value: userData.title as Title, validationResult: { passed: true } });
        };

        getUserData();
    }, [editMode]);

    const handleEditMode = () => {
        setEditMode(!editMode);
    };

    const closeAlert = () => {
        setAlertData((previousState) => {
            return {
                ...previousState,
                show: false,
            };
        });
    };

    const validateAllFields = () => {
        const validationResult = new Set([
            validateInput("firstName").passed,
            validateInput("lastName").passed,
            validateInput("title").passed,
            validateInput("address").passed,
            validateInput("mobilePhone").passed,
        ]);
        return validationResult.has(false);
    };

    const validateInput = (reference: UserSettingsFields) => {
        switch (reference) {
            case "firstName":
                return validators.IsStringNotEmptyValidator(firstName.value, "Enter First Name");
            case "lastName":
                return validators.IsStringNotEmptyValidator(lastName.value, "Enter Last Name");
            case "title":
                return validators.IsStringNotEmptyValidator(title.value, "Enter Title");
            case "address":
                return validators.IsStringNotEmptyValidator(address.value, "Enter Address");
            case "mobilePhone":
                return validators.MobilePhoneValidator(mobilePhone.value);
            default:
                throw Error(`Bad reference for validation - ${reference}`);
        }
    };

    const handleSave = async () => {
        const anyFieldInvalid = validateAllFields();

        if (!anyFieldInvalid) {
            if (fieldModified) {
                const newUserData: Omit<UpdateUserDataRequest, "userId"> = {
                    title: title.value,
                    firstName: firstName.value,
                    lastName: lastName.value,
                    address: address.value,
                    mobilePhone: mobilePhone.value,
                    custom_properties: JSON.stringify(customProperties),
                };
                const updateResult = await userService.updateUserById(newUserData);
                if (updateResult.errorMsg) {
                    if (updateResult.errorData?.length) {
                        updateResult.errorData.forEach((errorData: UpdateUserDataFieldValidationResult) => {
                            handleInputValidation(errorData.fieldName, { passed: false, error: errorData.errorMsg });
                        });
                    } else {
                        setAlertData({ show: true, text: updateResult.errorMsg });
                    }
                } else {
                    await userService.replaceUserInLocalStorage();
                    handleEditMode();
                }
            } else {
                handleEditMode();
            }
        }
    };

    const handleCancel = () => {
        handleEditMode();
        handleInputValidation("firstName", { passed: true });
        handleInputValidation("lastName", { passed: true });
        handleInputValidation("address", { passed: true });
        handleInputValidation("title", { passed: true });
        handleInputValidation("mobilePhone", { passed: true });
    };

    const handleInputValidation = (reference: UserSettingsFields, validationResult: { passed?: boolean; error?: string }) => {
        switch (reference) {
            case "firstName":
                setFirstName((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "lastName":
                setLastName((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "title":
                setTitle((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "address":
                setAddress((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            case "mobilePhone":
                setMobilePhone((previousState) => {
                    return {
                        ...previousState,
                        validationResult: validationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Validation - ${reference}`);
        }
    };

    const handleInputBlur = (reference: UserSettingsFields) => {
        const validationResult = validateInput(reference);
        handleInputValidation(reference, validationResult);
    };

    const handleAddressBoxChange = ({ address, addressDetails, addressLines }: ISelectedAddressRecord) => {
        setFieldModified(true);
        setAddress((previousState) => {
            const validationResult: ValidationResult = validators.IsStringNotEmptyValidator(address, "Enter Address");
            return {
                ...previousState,
                value: address,
                validationResult,
            };
        });
        setCustomProperties((previousState) => ({
            ...previousState,
            addressLine1: addressLines.line1,
            addressLine2: addressLines.line2,
            addressLine3: addressLines.line3,
            postCode: addressDetails.POSTCODE,
            postTown: addressDetails.TOWN_NAME,
            uprn: addressDetails.UPRN,
        }));
    };

    const handleInputChange = (value: string, reference: UserSettingsFields) => {
        setFieldModified(true);
        switch (reference) {
            case "firstName":
                setFirstName((previousState) => {
                    return {
                        ...previousState,
                        value: value,
                    };
                });

                break;
            case "lastName":
                setLastName((previousState) => {
                    const passwordValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter Last Name");
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordValidationResult,
                    };
                });
                break;
            case "title":
                setTitle((previousState) => {
                    const passwordComplianceValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter Title");
                    return {
                        ...previousState,
                        value: value as Title,
                        validationResult: passwordComplianceValidationResult,
                    };
                });
                break;
            case "mobilePhone":
                setMobilePhone((previousState) => {
                    const passwordComplianceValidationResult: ValidationResult = validators.IsStringNotEmptyValidator(value, "Enter Mobile Phone");
                    return {
                        ...previousState,
                        value: value,
                        validationResult: passwordComplianceValidationResult,
                    };
                });
                break;
            default:
                throw Error(`Bad reference for Input Change - ${reference}`);
        }
    };

    function generateErrorMessage(errorMessage: string) {
        return (
            <span style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                <ErrorSharp sx={{ width: "0.7em", height: "0.7em" }}></ErrorSharp>
                <span>{errorMessage}</span>
            </span>
        );
    }

    const getButtons = () => {
        if (editMode) {
            return (
                <>
                    <Button variant="contained" sx={{ width: 130 }} className="user-settings-page-button" onClick={async () => await handleSave()}>
                        Save
                    </Button>
                    <Button variant="outlined" onClick={() => handleCancel()}>
                        Cancel
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button variant="contained" sx={{ width: 130 }} className="user-settings-page-button" onClick={handleEditMode}>
                        Edit
                    </Button>
                    <Button variant="text" onClick={() => navigate(pp.ChangePasswordRoute())}>
                        Change password
                    </Button>
                </>
            );
        }
    };
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;

    return (
        <>
            <ResponsiveAppBar />
            <Board headerText="Profile Details" size="big">
                <form className="user-settings-page-form">
                    <TextField
                        sx={{ width: 300 }}
                        id="title"
                        label="Title"
                        error={!title.validationResult.passed}
                        helperText={title.validationResult.error && generateErrorMessage(title.validationResult?.error)}
                        onChange={(e) => handleInputChange(e.target.value, "title")}
                        onBlur={() => handleInputBlur("title")}
                        variant="outlined"
                        value={title.value}
                        disabled={!editMode}
                        required
                        select
                    >
                        {Object.values(Title).map((title) => {
                            return (
                                <MenuItem key={title} value={title}>
                                    {title}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <TextField
                        sx={{ width: 300 }}
                        id="first-name"
                        error={!firstName.validationResult.passed}
                        value={firstName.value}
                        helperText={firstName.validationResult.error && generateErrorMessage(firstName.validationResult?.error)}
                        onChange={(e) => handleInputChange(e.target.value, "firstName")}
                        onBlur={() => handleInputBlur("firstName")}
                        label="First Name"
                        variant="outlined"
                        disabled={!editMode}
                        required
                    />
                    <TextField
                        sx={{ width: 300 }}
                        id="last-name"
                        error={!lastName.validationResult.passed}
                        helperText={lastName.validationResult.error && generateErrorMessage(lastName.validationResult.error)}
                        onChange={(e) => handleInputChange(e.target.value, "lastName")}
                        onBlur={() => handleInputBlur("lastName")}
                        value={lastName.value}
                        label="Last Name"
                        variant="outlined"
                        disabled={!editMode}
                        required
                    />
                    <div className="user-settings-line-break"></div>
                    <AddressAutocomplete
                        localGazetteerUrl={clientSettings?.LOCAL_GAZETTEER_URL as string}
                        localGazetteerToken={clientSettings?.LOCAL_GAZETTEER_TOKEN as string}
                        nationalGazetteerUrl={clientSettings?.NATIONAL_GAZETTEER_URL as string}
                        nationalGazetteerToken={clientSettings?.NATIONAL_GAZETTEER_TOKEN as string}
                        style={{ width: 450, display: editMode ? "inline-flex" : "none" }}
                        gazetteer="national"
                        onSelect={handleAddressBoxChange}
                    />
                    <TextField
                        sx={{ width: 600 }}
                        id="address"
                        error={!address.validationResult.passed}
                        helperText={address.validationResult.error && generateErrorMessage(address.validationResult.error)}
                        onChange={(e) => handleInputChange(e.target.value, "address")}
                        onBlur={() => handleInputBlur("address")}
                        value={address.value}
                        label="Address"
                        variant="outlined"
                        disabled={!editMode}
                        required
                    />
                    <div className="user-settings-line-break"></div>
                    <TextField
                        sx={{ width: 300 }}
                        error={!mobilePhone.validationResult.passed}
                        helperText={mobilePhone.validationResult.error && generateErrorMessage(mobilePhone.validationResult.error)}
                        onChange={(e) => handleInputChange(e.target.value, "mobilePhone")}
                        onBlur={() => handleInputBlur("mobilePhone")}
                        id="phone-number"
                        value={mobilePhone.value}
                        label="Mobile Phone Number"
                        variant="outlined"
                        disabled={!editMode}
                        required
                    />
                </form>

                <div className="user-settings-button-container">{getButtons()}</div>
            </Board>
            <Box position="absolute" bottom="20px" left="20px" width="40%">
                {alertData.show ? (
                    <Alert severity="error" onClose={closeAlert}>
                        {alertData.text}
                    </Alert>
                ) : null}
            </Box>
        </>
    );
};

type UserSettingsFields = keyof UpdateUserDataRequest;

interface UserSettingsData {
    value: string;
    validationResult: {
        passed?: boolean;
        error?: string;
    };
}

interface UserTitleData {
    value: Title;
    validationResult: {
        passed?: boolean;
        error?: string;
    };
}

export default UserSettings;
